import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LuHouse, LuLock, LuMail } from 'react-icons/lu';
import { useAdminAuth } from '../context/AdminAuthContext';
import AdminThemeToggle from '../components/AdminThemeToggle';

const AdminLogin = () => {
  const [email, setEmail] = useState('admin@raadsotech.com');
  const [password, setPassword] = useState('Admin@12345');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.from?.pathname || '/admin';
  const sessionExpired = new URLSearchParams(location.search).get('expired') === '1';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-white transition-colors dark:bg-[#0a0a0a]">
      <div className="absolute right-5 top-5 z-30">
        <AdminThemeToggle />
      </div>

      <div className="grid min-h-screen lg:grid-cols-2">
        {/* Left branded panel */}
        <div className="relative hidden overflow-hidden bg-black lg:flex lg:flex-col lg:justify-between">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-black to-black" />
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 80%, rgba(243,199,21,0.35) 0%, transparent 45%), radial-gradient(circle at 80% 20%, rgba(243,199,21,0.15) 0%, transparent 40%)',
            }}
          />
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-black/80 to-transparent" />

          <div className="relative z-10 p-10">
            <img
              src="/img/logo-white.png"
              alt="RAADSO TECH"
              className="h-10 w-auto object-contain"
            />
          </div>

          <div className="relative z-10 px-10 pb-16">
            <h2 className="text-4xl font-extrabold leading-tight text-white md:text-5xl">
              Let&apos;s Kick
              <br />
              <span className="text-primary">Now!</span>
            </h2>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-gray-300">
              Sign in to manage your website content, services, and dashboard in less than 30 seconds.
            </p>
          </div>

          <div className="relative z-10 p-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white/80 transition-colors hover:text-primary"
            >
              <LuHouse size={14} />
              Home
            </Link>
          </div>
        </div>

        {/* Right panel */}
        <div className="relative flex min-h-screen flex-col items-center justify-center bg-[#fafafa] px-6 py-12 dark:bg-[#111111]">
          <div className="mb-6 flex w-full max-w-[420px] items-center gap-3 lg:hidden">
            <img src="/img/logo-black.png" alt="RAADSO TECH" className="h-8 w-auto object-contain dark:hidden" />
            <img src="/img/logo-white.png" alt="RAADSO TECH" className="hidden h-8 w-auto object-contain dark:block" />
            <p className="text-sm font-bold text-slate-700 dark:text-gray-200">Admin Portal</p>
          </div>

          <div className="pointer-events-none absolute right-[-80px] top-[-80px] h-64 w-64 rounded-full border border-primary/20" />
          <div className="pointer-events-none absolute right-[40px] top-[120px] h-40 w-40 rounded-full border border-primary/10" />
          <div className="pointer-events-none absolute bottom-[80px] right-[120px] h-3 w-3 rounded-full bg-primary/40" />

          <div className="relative z-10 w-full max-w-[420px] rounded-3xl border border-slate-200/80 bg-white px-8 py-10 shadow-[0_24px_60px_rgba(0,0,0,0.12)] dark:border-white/10 dark:bg-[#161616] dark:shadow-[0_24px_60px_rgba(0,0,0,0.45)] lg:-ml-24">
            <div className="mb-8 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.35em] text-slate-400 dark:text-gray-500">
                Sign In
              </p>
              <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-primary" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="relative">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@raadsotech.com"
                  className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-2 pr-8 text-slate-900 placeholder:text-slate-300 focus:border-primary focus:outline-none focus:ring-0 dark:border-white/20 dark:text-white dark:placeholder:text-gray-600"
                  required
                />
                <LuMail
                  size={18}
                  className="pointer-events-none absolute right-0 top-[34px] text-slate-400 dark:text-gray-500"
                />
              </div>

              <div className="relative">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full border-0 border-b border-slate-300 bg-transparent px-0 py-2 pr-8 text-slate-900 placeholder:text-slate-300 focus:border-primary focus:outline-none focus:ring-0 dark:border-white/20 dark:text-white dark:placeholder:text-gray-600"
                  required
                />
                <LuLock
                  size={18}
                  className="pointer-events-none absolute right-0 top-[34px] text-slate-400 dark:text-gray-500"
                />
              </div>

              {sessionExpired ? (
                <p className="rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                  Your session expired. Please sign in again.
                </p>
              ) : null}

              {error ? <p className="text-sm text-red-500 dark:text-red-400">{error}</p> : null}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-primary px-6 py-3.5 text-sm font-extrabold uppercase tracking-wide text-black shadow-[0_10px_30px_rgba(243,199,21,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-70"
              >
                {loading ? 'Signing in...' : 'Get Started'}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-slate-500 dark:text-gray-400">
              Back to website?{' '}
              <Link to="/" className="font-bold text-primary hover:underline">
                Visit Home
              </Link>
            </p>
          </div>

          <p className="absolute bottom-6 right-6 hidden text-xs text-slate-400 dark:text-gray-600 lg:block">
            © {new Date().getFullYear()} RAADSO TECH
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
